/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { notEmpty } from '@ember/object/computed';
import { edgesEndingInExitCount } from 'cio/utils/visual-workflow-builder/branching';
import { isNone, isEmpty } from '@ember/utils';
import ActionModel from 'cio/models/action';
import CampaignModel, { ParsedTreeBranchNode } from 'cio/models/campaign';
import ActionSubjectsService, { SUBJECT_COUNT_LIMIT } from 'cio/services/action-subjects';
import AnalyticsService from 'cio/services/analytics';
import { WorkflowGraph } from 'cio/utils/visual-workflow-builder/workflow-graph';
import { DragMode, VWBTab } from 'cio/utils/visual-workflow-builder/types';
import { Periods } from 'cio/utils/metrics/common';
import { ClonePluginOptions } from 'fly-drag-drop/plugins/draggable-clone';
import { AutoscrollPluginOptions } from 'fly-drag-drop/plugins/autoscroll';
import { cached, tracked } from '@glimmer/tracking';
import { Dropdown as BasicDropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import { TimeRange } from 'cio/utils/time';
import { ClickSource } from 'cio/utils/analytics/transforms/campaign';
import { BatchUpdateTemplateType } from 'cio/utils/actions/batch-update';
import isMultiLangBranchChild from 'cio/utils/visual-workflow-builder/is-multi-lang-branch-child';
import TemplateModel from 'cio/models/template';
import DS from 'ember-data';
import { isCustomPush, isCustomPushPayloadEmpty } from 'cio/utils/push/custom-payload';
import { dependentKeyCompat } from '@ember/object/compat';

interface VisualWorkflowBuilderWorkflowActionArgs {
  action: ActionModel;
  item: any;
  selectedAction: ActionModel;
  campaign: CampaignModel;
  badge: any;
  readOnly: boolean;
  isCampaignOverview: boolean;
  isEditing: boolean;
  isGrayedOut: boolean;
  isEditingParent: boolean;
  isSaving: boolean;
  isVariation?: boolean;
  isFullWidth?: boolean;
  index: number;
  lastIndex: boolean;
  branch: ParsedTreeBranchNode;
  branchId: string;
  pathIndex: number;
  shouldClampTimeWindowDescription?: boolean;
  tab: VWBTab;
  metricsTabSelectedMeasures: string[];
  metricsTabTimePeriod: Periods;
  metricsTabTimeRange: TimeRange;
  subActions?: any[];
  onSelect: (action: ActionModel, event?: MouseEvent) => unknown;
  onDragStart: (action: ActionModel) => unknown;
  onDragEnd: () => unknown;
  onDrag: (opts: any) => unknown;
  onDestroyed: () => unknown;
  x?: number;
  y?: number;
  highlightedNodeIds: string[];
  workflowGraph: WorkflowGraph;
  onActionClicked: (event: MouseEvent, action: ActionModel) => unknown;
  canDrag: boolean;
  shouldShowUnhighlightableTooltip: boolean;
  dragMode: DragMode;
  canCopy: boolean;
  canMove: boolean;
  hasReadOnlyPermission: boolean;
}

const DRAGGABLE_CLONE_PLUGIN_OPTIONS: Partial<ClonePluginOptions> = {
  classes: ['js-vwb-workflow-action-mirror'],
  appendContainer: 'body',
  initialOffset: null,
  cloneElement: null,
  scaleWithTransform: true,
  transparentizeOriginal: true,
};

const AUTOSCROLL_PLUGIN_OPTIONS: Partial<AutoscrollPluginOptions> = {
  scrollableElement: '.js-vwb-canvas',
};

export default class VisualWorkflowBuilderWorkflowAction extends Component<VisualWorkflowBuilderWorkflowActionArgs> {
  @service actionSubjects!: ActionSubjectsService;
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;
  @tracked template?: TemplateModel;

  readonly subjectCountLimit = SUBJECT_COUNT_LIMIT;

  BatchUpdateTemplateType = BatchUpdateTemplateType;

  isHoveringOver = false;
  unhighlightableTooltipDropdown: BasicDropdown | null = null;

  constructor(owner: unknown, args: VisualWorkflowBuilderWorkflowActionArgs) {
    super(owner, args);
    this.peekOrFetchTemplate();
  }

  @action
  async peekOrFetchTemplate() {
    let { templateId } = this.args.action;
    if (templateId) {
      this.template =
        this.store.peekRecord('template', templateId) ?? (await this.store.findRecord('template', templateId));
    }
  }

  @action
  onInsertBasicDropdown(_element: HTMLElement, [dropdown]: [BasicDropdown]) {
    this.unhighlightableTooltipDropdown = dropdown;
  }

  @action
  onBasicDropdownOpen() {
    return this.args.shouldShowUnhighlightableTooltip;
  }

  @action
  onShouldshowUnhighlightableTooltipChange() {
    if (!this.unhighlightableTooltipDropdown) {
      return;
    }

    if (
      this.args.shouldShowUnhighlightableTooltip &&
      this.isHoveringOver &&
      !this.unhighlightableTooltipDropdown.isOpen
    ) {
      this.unhighlightableTooltipDropdown.actions.open();
    } else {
      this.unhighlightableTooltipDropdown.actions.close();
    }
  }

  @action
  onMouseEnterTrigger() {
    this.isHoveringOver = true;

    if (this.args.shouldShowUnhighlightableTooltip) {
      this.unhighlightableTooltipDropdown?.actions.open();
      return true;
    }

    return false;
  }

  @action
  onMouseLeaveTrigger() {
    this.isHoveringOver = false;
    this.unhighlightableTooltipDropdown?.actions.close();
  }

  @computed('args.action.{isMessagingType,isAttributeAction,isManualSegmentUpdateAction,isCollectionAction}')
  get showSendingBehaviorIcon() {
    let { isMessagingType, isAttributeAction, isManualSegmentUpdateAction, isCollectionAction } = this.args.action;
    return isMessagingType && !isAttributeAction && !isManualSegmentUpdateAction && !isCollectionAction;
  }

  get isEditingThis() {
    return this.args.action === this.args.selectedAction;
  }

  @computed('args.readOnly', 'args.action.isDeleting')
  get readOnly() {
    return this.args.readOnly || this.args.action.isDeleting;
  }

  @computed(
    'args.isSaving',
    'args.canDrag',
    'readOnly',
    'args.isVariation',
    'args.campaign.branchTree',
    'args.action.isBranch',
    'args.canCopy',
    'args.canMove',
    'args.highlightedNodeIds.[]',
  )
  get canDrag() {
    let canDrag =
      !this.args.isSaving &&
      !this.readOnly &&
      !this.args.isVariation &&
      this.args.canDrag &&
      !this.args.action.isDeleting;

    // Only enter conditional if item is otherwise draggable.
    // We don't need to check for exit edges if this item isn't draggable.
    if (canDrag && this.args.action.isBranch) {
      // If all branches end in exit, the action cannot be dragged.
      if (edgesEndingInExitCount(this.args.item) === this.args.item.edges.length) {
        return (this.args.highlightedNodeIds ?? []).length > 0 && (this.args.canCopy || this.args.canMove);
      }

      return canDrag;
    }

    return canDrag;
  }

  @computed('args.action.id')
  get subjects() {
    return this.actionSubjects.find(this.args.action.id);
  }

  @computed('args.action.isSplitTest', 'args.action.info.group')
  get groupTypeClass() {
    if (this.args.action.isSplitTest) {
      return 'vwb-workflow-action--split';
    }

    return `vwb-workflow-action--${this.args.action.info.group ?? 'default'}`;
  }

  @notEmpty('args.action.warnings') hasActionWarning!: boolean;

  @dependentKeyCompat
  @cached
  get invalidCollectionAction() {
    if (!this.args.action.isCollectionAction) {
      return false;
    }
    let { action } = this.args;

    return isNone(action.belongsTo('collection').id()) || isEmpty(action.attributeName);
  }

  get invalidBatchUpdateAction() {
    if (!this.args.action.isBatchUpdateAction) {
      return false;
    }

    let { action } = this.args;

    if (action.templateType === BatchUpdateTemplateType.AttributeUpdate) {
      return action.hasNoContent;
    }

    if (action.templateType === BatchUpdateTemplateType.CreateEvent) {
      return isEmpty(action.eventName);
    }

    return true;
  }

  @computed(
    'hasActionWarning',
    'invalidCollectionAction',
    'invalidBatchUpdateAction',
    'args.action.info.{showNoContent,showNoSubject,showRequest}',
    'args.action.{hasNoContent,hasNoSubject,hasNoRequest,isManualSegmentUpdateAction,segment,attributeName,isCreateEventAction,eventName,holdout}',
  )
  get hasIncompleteConfiguration() {
    let { action } = this.args;

    if (this.args.action.holdout) {
      return false;
    }

    return (
      this.hasActionWarning ||
      /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
      (action.info.showNoSubject && action.hasNoSubject) ||
      (action.info.showNoContent && action.hasNoContent) ||
      (action.info.showRequest && action.hasNoRequest) ||
      /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
      (action.isManualSegmentUpdateAction ? isNone(action.belongsTo('segment').id()) : false) ||
      (action.isCreateEventAction && isEmpty(action.eventName)) ||
      this.invalidBatchUpdateAction ||
      this.invalidCollectionAction
    );
  }

  get hasEmptyCustomPushPayload() {
    let { template } = this;
    if (isNone(template)) {
      return false;
    }
    return isCustomPush(template) ? isCustomPushPayloadEmpty(template) : false;
  }

  get isShowingMetrics() {
    return this.args.tab === VWBTab.Metrics;
  }

  @computed('args.action.{isMessagingType,isAttributeAction,isManualSegmentUpdateAction,isCollectionAction}')
  get canShowMetrics() {
    let { action } = this.args;
    return (
      action.isMessagingType &&
      !action.isAttributeAction &&
      !action.isManualSegmentUpdateAction &&
      !action.isCollectionAction
    );
  }

  get isMultiLangBranchChildAction() {
    return isMultiLangBranchChild(this.args.action);
  }

  @action
  onClick(event: MouseEvent) {
    if (this.args.onActionClicked) {
      this.args.onActionClicked(event, this.args.action);
    }

    if (this.readOnly || this.args.isSaving) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    event.stopPropagation();

    if (!this.args.action.isSplitTest) {
      this.analytics.track('campaign', 'workflow_existing_item_clicked', {
        model: this.args.campaign,
        action: this.args.action,
        trayTab: this.args.tab,
        source: ClickSource.WorkflowItem,
      });
    }

    this.args.onSelect(this.args.action, event);
  }

  @action
  onBeforeDragStart() {
    this.updatePluginOptions();
  }

  @action
  onDragStart() {
    this.args.onDragStart(this.args.action);
  }

  @computed('args.dragMode', 'args.highlightedNodeIds.[]', 'args.action.id')
  get copyHighlighted() {
    return this.args.dragMode === DragMode.Copy && (this.args.highlightedNodeIds ?? []).includes(this.args.action.id);
  }

  @computed('args.dragMode', 'args.highlightedNodeIds.[]', 'args.action.id')
  get moveHighlighted() {
    return this.args.dragMode === DragMode.Move && (this.args.highlightedNodeIds ?? []).includes(this.args.action.id);
  }

  @tracked pluginOptions = {
    clone: { ...DRAGGABLE_CLONE_PLUGIN_OPTIONS },
    autoscroll: { ...AUTOSCROLL_PLUGIN_OPTIONS },
  };

  @action
  updatePluginOptions() {
    let cloneOptions: Partial<ClonePluginOptions> = {
      ...DRAGGABLE_CLONE_PLUGIN_OPTIONS,
      transparentizeOriginal: this.args.dragMode === DragMode.Move,
    };

    if (this.copyHighlighted) {
      cloneOptions.cloneElement = '.js-vwb-copy-highlighted-nodes-mirror';
      cloneOptions.scaleWithTransform = false;
      cloneOptions.initialOffset = () => ({
        x: 100,
        y: 20,
      });
    } else if (this.moveHighlighted) {
      cloneOptions.cloneElement = '.js-vwb-move-highlighted-nodes-mirror';
      cloneOptions.scaleWithTransform = false;
      cloneOptions.initialOffset = () => ({
        x: 100,
        y: 20,
      });
    } else {
      if (this.args.action.isBranch && this.args.dragMode !== DragMode.Copy) {
        cloneOptions.cloneElement = `.js-clone-node-${this.args.action.id}`;
        cloneOptions.scaleWithTransform = false;
        cloneOptions.initialOffset = () => ({
          x: 150,
          y: 40,
        });
      } else {
        // scaleWithTransform does not work well with a cloneElement
        cloneOptions.scaleWithTransform = true;
      }
    }

    this.pluginOptions = {
      clone: cloneOptions,
      autoscroll: { ...AUTOSCROLL_PLUGIN_OPTIONS },
    };
  }
}
